import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import apiFetch from '../utils/apiFetch';
import { API_BASE_URL } from '../config';

/**
 * 運営が切り替える設定。
 *
 * いま置いてあるのは「AIが数えた種類別台数を、人の確認なしに公開するか」の
 * 元栓ひとつ。**既定は off**。
 *
 * この画面がある理由は、判断に日付と名前を付けるため。「当分は自動公開しない」を
 * コードの定数で持つと、変えるのがデプロイになり、外からはいまどちらなのかも
 * 分からない。
 */
export default function TypeCountSettings() {
  useAuthGuard();

  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/settings`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setRows(body.data || []);
    } catch (e) {
      setError('設定を読み込めませんでした: ' + e.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (row) => {
    const next = row.value === 'true' ? 'false' : 'true';
    // **元栓を開けるほうだけ確認を挟む。** 閉じるのはいつでも安全。
    if (next === 'true') {
      const ok = window.confirm(
        'AIが数えた台数が、人の確認なしに店舗ページへ出るようになります。\n' +
          'カバレッジは未確定で、同じ区画を別のコマで二重に数えたものが混ざります。\n' +
          '開けてよいですか。',
      );
      if (!ok) return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/settings/${encodeURIComponent(row.key)}`, {
        method: 'PUT',
        body: JSON.stringify({ value: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      setError('切り替えられませんでした: ' + e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <h1 className="h4 mb-3">設定</h1>
      {error && <div className="alert alert-danger">{error}</div>}

      {rows.length === 0 && !error && (
        <p className="text-muted">設定はまだ1つもありません。</p>
      )}

      {rows.map((row) => (
        <div className="card mb-3" key={row.key}>
          <div className="card-body">
            <div className="d-flex align-items-center gap-2">
              <code style={{ fontSize: '0.8125rem' }}>{row.key}</code>
              <span
                className={`badge bg-${row.value === 'true' ? 'success' : 'secondary'}`}
              >
                {row.value === 'true' ? 'on' : 'off'}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-phoenix-secondary ms-auto"
                disabled={busy}
                onClick={() => toggle(row)}
              >
                {row.value === 'true' ? 'off にする' : 'on にする'}
              </button>
            </div>
            {row.note && (
              <p className="mb-0 mt-2 text-muted" style={{ fontSize: '0.8125rem' }}>
                {row.note}
              </p>
            )}
            {row.updatedAt && (
              <p className="mb-0 mt-1 text-muted" style={{ fontSize: '0.75rem' }}>
                最終更新 {new Date(row.updatedAt).toLocaleString('ja-JP')}
              </p>
            )}
          </div>
        </div>
      ))}
    </Layout>
  );
}
